import { Link, useSearchParams } from 'react-router-dom';
import { burgerRestaurants } from '../../mocks/restaurants';
import { useState, useEffect } from 'react';
import { useSEO } from '../../utils/seo';
import { supabase } from '../../utils/supabaseClient';

interface Comment {
  id: number;
  restaurant_id?: number;
  name: string;
  text: string;
  date: string;
  created_at?: string;
}

export default function BurgerRestaurants() {
  const [searchParams] = useSearchParams();
  const [startIndex, setStartIndex] = useState(0);
  const [selectedRestaurant, setSelectedRestaurant] = useState<number | null>(null);
  const [glowingRestaurant, setGlowingRestaurant] = useState<number | null>(null);
  const [expandedComments, setExpandedComments] = useState<number | null>(null);
  const [comments, setComments] = useState<Record<number, Comment[]>>({});
  const [commentForms, setCommentForms] = useState<Record<number, { name: string; text: string }>>({});
  const itemsPerPage = 6;
  const displayedRestaurants = burgerRestaurants.slice(startIndex, startIndex + itemsPerPage);
  const hasMore = startIndex + itemsPerPage < burgerRestaurants.length;

  useSEO({
    title: 'Best Burger Restaurants Antalya Juicy Gourmet Burgers Guide',
    description: 'Discover the best burger restaurants in Antalya serving juicy gourmet burgers, craft beers, and American-style dining. Find top-rated burger restaurants in Antalya with reviews, ratings, locations, and contact information for an unforgettable dining experience.',
    keywords: 'burger restaurants Antalya, best burgers Antalya, gourmet burgers',
    canonical: '/burger-restaurants',
    ogTitle: 'Best Burger Restaurants in Antalya',
    ogDescription: 'Find the juiciest burgers in Antalya',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'name': 'Burger Restaurants in Antalya',
      'url': '{{SITE_URL}}/burger-restaurants',
      'description': 'Find the best burger restaurants in Antalya',
      'mainEntity': {
        '@type': 'ItemList',
        'itemListElement': burgerRestaurants.map((restaurant, index) => ({
          '@type': 'ListItem',
          'position': index + 1,
          'item': {
            '@type': 'Restaurant',
            'name': restaurant.name,
            'description': restaurant.description,
            'address': {
              '@type': 'PostalAddress',
              'streetAddress': restaurant.address,
              'addressLocality': 'Antalya',
              'addressCountry': 'TR'
            },
            'telephone': restaurant.phone,
            'priceRange': restaurant.priceRange,
            'aggregateRating': {
              '@type': 'AggregateRating',
              'ratingValue': restaurant.rating,
              'bestRating': '5'
            },
            'servesCuisine': 'American'
          }
        }))
      }
    }
  });

  useEffect(() => {
    const restaurantId = searchParams.get('restaurant');
    if (restaurantId) {
      const id = parseInt(restaurantId);
      setGlowingRestaurant(id);
      
      // Scroll to the restaurant
      setTimeout(() => {
        const element = document.getElementById(`restaurant-${id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
      // Remove glow after 2 seconds
      setTimeout(() => {
        setGlowingRestaurant(null);
      }, 2000);
    }
  }, [searchParams]);

  // Load comments from Supabase
  useEffect(() => {
    loadComments();
  }, []);

  const loadComments = async () => {
    try {
      const { data, error } = await supabase
        .from('comments')
        .select('*')
        .eq('restaurant_category', 'burger')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        const groupedComments: Record<number, Comment[]> = {};
        data.forEach((comment: any) => {
          const restaurantId = comment.restaurant_id;
          if (!groupedComments[restaurantId]) {
            groupedComments[restaurantId] = [];
          }
          groupedComments[restaurantId].push({
            id: comment.id,
            restaurant_id: comment.restaurant_id,
            name: comment.name,
            text: comment.text,
            date: new Date(comment.created_at).toLocaleDateString(),
            created_at: comment.created_at
          });
        });
        setComments(groupedComments);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const handleLoadMore = () => {
    if (hasMore) {
      setStartIndex(prev => prev + itemsPerPage);
    }
  };

  const toggleRestaurant = (id: number) => {
    setSelectedRestaurant(prev => prev === id ? null : id);
  };

  const toggleComments = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedComments(prev => prev === id ? null : id);
  };

  const handleCommentChange = (restaurantId: number, field: 'name' | 'text', value: string) => {
    setCommentForms(prev => ({
      ...prev,
      [restaurantId]: {
        ...prev[restaurantId],
        [field]: value
      }
    }));
  };

  const handleCommentSubmit = async (restaurantId: number, e: React.FormEvent) => {
    e.preventDefault();
    const form = commentForms[restaurantId];
    if (!form?.name || !form?.text) return;

    try {
      const { data, error } = await supabase
        .from('comments')
        .insert([
          {
            restaurant_id: restaurantId,
            restaurant_category: 'burger',
            name: form.name,
            text: form.text
          }
        ])
        .select();

      if (error) throw error;

      if (data && data[0]) {
        const newComment: Comment = {
          id: data[0].id,
          restaurant_id: data[0].restaurant_id,
          name: data[0].name,
          text: data[0].text,
          date: new Date(data[0].created_at).toLocaleDateString(),
          created_at: data[0].created_at
        };

        setComments(prev => ({
          ...prev,
          [restaurantId]: [newComment, ...(prev[restaurantId] || [])]
        }));

        setCommentForms(prev => ({
          ...prev,
          [restaurantId]: { name: '', text: '' }
        }));
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
      alert('Failed to post comment. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white border-b-4 border-black z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="https://public.readdy.ai/ai/img_res/46496767-ecf9-45a1-9f97-898f0428276b.png" 
              alt="Antalya Eats Logo" 
              className="w-8 h-8 object-contain image-rendering-pixelated"
              style={{ imageRendering: 'pixelated' }}
            />
            <span className="text-lg font-bold tracking-tight">ANTALYA EATS</span>
          </Link>
          <nav className="flex gap-4">
            <Link to="/" className="text-sm font-medium hover:underline decoration-2 underline-offset-4">Home</Link>
            <Link to="/about" className="text-sm font-medium hover:underline decoration-2 underline-offset-4">About</Link>
          </nav>
        </div>
      </header>

      {/* Page Header */}
      <section className="pt-20 pb-8 px-4 bg-yellow-400 border-b-4 border-black">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="w-32 h-32 mx-auto mb-3">
            <img 
              src="https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/de26e89533d24c0934ba29e2e863c70a.png"
              alt="Delicious Burger"
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-5xl font-bold text-black mb-2">BURGER RESTAURANTS</h1>
          <p className="text-base text-black">Find the <strong>juiciest burgers</strong> in Antalya</p>
        </div>
      </section>

      {/* Restaurants List */}
      <main>
      <section className="py-8 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="grid gap-4">
            {burgerRestaurants.map((restaurant) => (
              <article 
                key={restaurant.id}
                id={`restaurant-${restaurant.id}`}
                onClick={() => toggleRestaurant(restaurant.id)}
                className={`bg-white border-4 border-black p-4 hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all cursor-pointer relative ${
                  glowingRestaurant === restaurant.id ? 'animate-glow' : ''
                }`}
                style={glowingRestaurant === restaurant.id ? {
                  boxShadow: '0 0 20px 5px rgba(234, 179, 8, 0.8), 8px 8px 0px 0px rgba(0,0,0,1)',
                  animation: 'glow 0.5s ease-in-out 4'
                } : undefined}
              >
                <div className="flex flex-col gap-3">
                  <div className="flex-1">
                    <h2 className="text-xl font-bold mb-2">{restaurant.name}</h2>
                    <p className="text-gray-600 text-sm mb-3">{restaurant.description}</p>
                    <div className="flex flex-col gap-2 text-xs">
                      <div className="flex items-center gap-2">
                        <i className="ri-map-pin-line text-base"></i>
                        <span>{restaurant.address}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <i className="ri-phone-line text-base"></i>
                        <a href={`tel:${restaurant.phone}`} className="hover:text-yellow-600 transition-colors cursor-pointer" onClick={(e) => e.stopPropagation()}>
                          {restaurant.phone}
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <i className="ri-star-fill text-yellow-400 text-lg"></i>
                      <span className="text-lg font-bold">{restaurant.rating}</span>
                    </div>
                    <div className="text-base font-bold text-yellow-600">{restaurant.priceRange}</div>
                  </div>
                </div>

                {/* Restaurant Image Popup */}
                {selectedRestaurant === restaurant.id && (
                  <div className="mt-4 pt-4 border-t-4 border-black">
                    <div className="w-full h-48 bg-gray-100 border-4 border-black overflow-hidden">
                      <img 
                        src={restaurant.id === 2 
                          ? "https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/c8834c44a1a6ae9ce82b835bbe19831c.jpeg"
                          : restaurant.id === 3
                          ? "https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/781c57e5e92db693136d7f1537366b48.jpeg"
                          : restaurant.id === 4
                          ? "https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/68450ba7bfc7e8696d18daa701644b7a.jpeg"
                          : restaurant.id === 5
                          ? "https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/5b3539cb3ede8617ac339e42e3c258ba.jpeg"
                          : restaurant.id === 6
                          ? "https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/373912eaa96a16ae386bc1102f19eaf5.png"
                          : "https://static.readdy.ai/image/1681d69a5b156bf16fd7f9a96245b161/b527156683a6cc670134c1e942ff4df3.jpeg"
                        }
                        alt={restaurant.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}

                {/* Comments Section */}
                <div className="mt-4 pt-4 border-t-4 border-black">
                  <button
                    onClick={(e) => toggleComments(restaurant.id, e)}
                    className="w-full flex items-center justify-between text-left font-bold text-sm hover:text-yellow-600 transition-colors"
                  >
                    <span>COMMENTS ({comments[restaurant.id]?.length || 0})</span>
                    <i className={`ri-arrow-down-s-line text-xl transition-transform ${expandedComments === restaurant.id ? 'rotate-180' : ''}`}></i>
                  </button>

                  {expandedComments === restaurant.id && (
                    <div className="mt-4" onClick={(e) => e.stopPropagation()}>
                      {/* Comment Form */}
                      <form onSubmit={(e) => handleCommentSubmit(restaurant.id, e)} className="mb-4">
                        <input
                          type="text"
                          placeholder="Your name"
                          value={commentForms[restaurant.id]?.name || ''}
                          onChange={(e) => handleCommentChange(restaurant.id, 'name', e.target.value)}
                          className="w-full px-3 py-2 border-4 border-black text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                          required
                        />
                        <textarea
                          placeholder="Write your comment..."
                          value={commentForms[restaurant.id]?.text || ''}
                          onChange={(e) => handleCommentChange(restaurant.id, 'text', e.target.value)}
                          maxLength={200}
                          className="w-full px-3 py-2 border-4 border-black text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-yellow-400 resize-none"
                          rows={3}
                          required
                        />
                        <button
                          type="submit"
                          className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-sm px-4 py-2 border-4 border-black transition-colors whitespace-nowrap cursor-pointer"
                        >
                          POST COMMENT
                        </button>
                      </form>

                      {/* Comments List */}
                      {comments[restaurant.id] && comments[restaurant.id].length > 0 && (
                        <div className="space-y-3">
                          {comments[restaurant.id].map((comment) => (
                            <div key={comment.id} className="bg-gray-50 border-4 border-black p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-bold text-sm">{comment.name}</span>
                                <span className="text-xs text-gray-500">{comment.date}</span>
                              </div>
                              <p className="text-sm text-gray-700">{comment.text}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>

          {/* Buttons */}
          <div className="mt-8 flex justify-center gap-4">
            <Link 
              to="/"
              className="inline-block bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-base px-6 py-3 border-4 border-black transition-colors whitespace-nowrap cursor-pointer"
            >
              ← BACK TO CATEGORIES
            </Link>
            {hasMore && (
              <button
                onClick={handleLoadMore}
                className="inline-block bg-yellow-400 hover:bg-yellow-500 text-black font-bold text-base px-6 py-3 border-4 border-black transition-colors whitespace-nowrap cursor-pointer"
              >
                MORE →
              </button>
            )}
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8 px-4 border-t-4 border-black">
        <div className="container mx-auto text-center">
          <p className="text-sm mb-2">© 2025 Antalya Eats. Your guide to the best restaurants in Antalya.</p>
          <a href="https://readdy.ai/?ref=logo" className="text-xs text-gray-400 hover:text-white whitespace-nowrap">Web Design</a>
        </div>
      </footer>
    </div>
  );
}
